'use client';

import { useState, useEffect, useRef } from 'react';


import {
  Eye,
  Heart,
  Clock,
  MessageSquare,
  Globe,
  Zap,
  Target,
  Users,
  ChevronDown,
  Upload,
  CheckCircle,
  ArrowRight,
  Briefcase,
  MapPin,
  Filter,
} from 'lucide-react';

/* ─── Intersection Observer Hook ─── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

/* ─── Animated Section Wrapper ─── */
function Reveal({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0px)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Country codes ─── */
const COUNTRY_CODES = [
  { code: '+1', label: 'US/CA' },
  { code: '+44', label: 'UK' },
  { code: '+49', label: 'DE' },
  { code: '+33', label: 'FR' },
  { code: '+48', label: 'PL' },
  { code: '+31', label: 'NL' },
  { code: '+34', label: 'ES' },
  { code: '+39', label: 'IT' },
  { code: '+61', label: 'AU' },
  { code: '+91', label: 'IN' },
  { code: '+55', label: 'BR' },
  { code: '+52', label: 'MX' },
  { code: '+971', label: 'UAE' },
  { code: '+966', label: 'SA' },
  { code: '+20', label: 'EG' },
  { code: '+27', label: 'ZA' },
  { code: '+82', label: 'KR' },
  { code: '+81', label: 'JP' },
  { code: '+86', label: 'CN' },
  { code: '+7', label: 'RU' },
];

/* ─── Values ─── */
const VALUES = [
  {
    Icon: Eye,
    title: 'Clarity Over Complexity',
    desc: 'We strip away noise. Every feature, every decision, every sentence — if it doesn\'t make things clearer, it doesn\'t belong.',
    color: '#F97316',
  },
  {
    Icon: Heart,
    title: 'Ownership Without Ego',
    desc: 'You own your work end-to-end. That means the wins and the mistakes. No blame culture — just accountability and growth.',
    color: '#8B5CF6',
  },
  {
    Icon: Clock,
    title: 'Respect for People\'s Time',
    desc: 'Async by default. No unnecessary meetings. We write things down, move fast, and trust each other to get things done.',
    color: '#06B6D4',
  },
  {
    Icon: MessageSquare,
    title: 'Honest by Default',
    desc: 'We say what we mean. Feedback is direct and kind. We\'d rather have a hard conversation than let a problem fester.',
    color: '#10B981',
  },
];

/* ─── Hiring Steps ─── */
const STEPS = [
  { num: '01', title: 'Application Review', desc: 'We read every application personally. No automated rejections. If your background fits, we\'ll reach out within 5 business days.' },
  { num: '02', title: 'Intro Call', desc: '15–20 minutes with a team member. We want to understand your background, what you\'re looking for, and answer your questions.' },
  { num: '03', title: 'Skill / Case Assessment', desc: 'A focused, practical task relevant to the role. We respect your time — it\'s designed to take 2–3 hours, not a week.' },
  { num: '04', title: 'Team Interview', desc: 'Meet 2–3 people you\'d work with directly. A real conversation about how you think, work, and approach problems.' },
  { num: '05', title: 'Final Decision', desc: 'We move fast. You\'ll hear back within 3 business days of your final interview — no ghosting, ever.' },
];

/* ─── Why Join ─── */
const WHY_JOIN = [
  { Icon: Globe, title: 'Remote-first team', desc: 'Work from anywhere. We\'re distributed across timezones and built for async collaboration.' },
  { Icon: Target, title: 'High ownership & autonomy', desc: 'You\'ll own real problems, not just tickets. Your decisions shape the product.' },
  { Icon: Zap, title: 'Real impact on product direction', desc: 'We\'re small enough that your voice matters. Good ideas get shipped, not buried.' },
  { Icon: Users, title: 'Fast decision-making', desc: 'No 6-layer approval chains. We decide, we ship, we learn. Speed is a feature.' },
  { Icon: MessageSquare, title: 'No unnecessary meetings', desc: 'Async first. Meetings only when they\'re the best tool. Your calendar stays yours.' },
];

/* ─── Departments / Locations / Types ─── */
const DEPARTMENTS = ['All Departments', 'Engineering', 'Product', 'Marketing', 'Design', 'Operations'];
const LOCATIONS = ['All Locations', 'Remote', 'Europe', 'Americas', 'Asia-Pacific'];
const TYPES = ['All Types', 'Full-time', 'Part-time', 'Contract', 'Internship'];

/* ─── Input style helper ─── */
const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '12px 16px',
  color: 'white',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s',
};

export default function CareersPage() {
  const [dept, setDept] = useState('All Departments');
  const [loc, setLoc] = useState('All Locations');
  const [type, setType] = useState('All Types');

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    countryCode: '+1',
    phone: '',
    linkedin: '',
    motivation: '',
    cv: null as File | null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const jobsRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email is required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    if (!form.linkedin.trim()) e.linkedin = 'LinkedIn or portfolio URL is required';
    if (!form.motivation.trim() || form.motivation.trim().length < 30) e.motivation = 'Please write at least 30 characters';
    if (!form.cv) e.cv = 'Please upload your CV or resume';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    // Simulate submission
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    setSubmitted(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setForm((f) => ({ ...f, cv: file }));
    if (file) setErrors((er) => ({ ...er, cv: '' }));
  };

  /* ─── Animated gradient orbs ─── */
  const heroOrbs = (
    <>
      <div style={{ position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '900px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(249,115,22,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '44px 44px', WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, black, transparent)', maskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, black, transparent)', pointerEvents: 'none' }} />
    </>
  );

  return (
    <div style={{ background: '#060B14', minHeight: '100vh', color: 'white' }}>

      {/* ── HERO ── */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '100px 24px 80px', textAlign: 'center' }}>
        {heroOrbs}
        <div style={{ position: 'relative', maxWidth: '820px', margin: '0 auto' }}>
          <Reveal>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '20px' }}>
              Careers at Incade
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 style={{ fontSize: 'clamp(36px, 5.5vw, 64px)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.04, color: 'white', marginBottom: '24px' }}>
              Join the team building the{' '}
              <span style={{ background: 'linear-gradient(135deg, #F97316, #FB923C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                future of ad intelligence
              </span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p style={{ fontSize: '18px', color: '#94A3B8', maxWidth: '580px', margin: '0 auto 40px', lineHeight: 1.65 }}>
              We&apos;re a remote-first team helping performance marketers make smarter decisions. If you care about clarity, speed, and real impact — you&apos;ll fit right in.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => scrollTo(jobsRef)}
                style={{ background: 'linear-gradient(135deg,#F97316,#EA580C)', color: 'white', padding: '13px 28px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
              >
                View Open Roles <ChevronDown size={16} />
              </button>
              <button
                onClick={() => scrollTo(formRef)}
                style={{ background: 'transparent', color: 'white', padding: '13px 28px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(249,115,22,0.4)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(249,115,22,0.06)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                Apply Now
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section style={{ padding: '88px 24px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '14px' }}>Our Values</span>
              <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1 }}>
                What we stand for
              </h2>
            </div>
          </Reveal>

          {/* Bento-style asymmetric grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '16px' }}>
            {VALUES.map((v, i) => {
              const spans = [7, 5, 5, 7];
              return (
                <Reveal key={v.title} delay={i * 80} className={`col-span-12 md:col-span-${spans[i]}`} style={{ gridColumn: `span ${spans[i]}` } as React.CSSProperties}>
                  <div
                    className="values-card"
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: '20px',
                      padding: '36px',
                      height: '100%',
                      transition: 'border-color 0.3s, background 0.3s, transform 0.3s',
                      cursor: 'default',
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.borderColor = `${v.color}33`;
                      el.style.background = `${v.color}08`;
                      el.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.borderColor = 'rgba(255,255,255,0.06)';
                      el.style.background = 'rgba(255,255,255,0.02)';
                      el.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: `${v.color}18`, border: `1px solid ${v.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                      <v.Icon size={24} style={{ color: v.color }} />
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'white', marginBottom: '10px', letterSpacing: '-0.02em' }}>{v.title}</h3>
                    <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.65 }}>{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HIRING PROCESS ── */}
      <section style={{ padding: '88px 24px', background: 'rgba(255,255,255,0.01)', borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '14px' }}>Hiring Process</span>
              <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1 }}>
                What to expect
              </h2>
            </div>
          </Reveal>

          {/* Horizontal scrollable steps on mobile, grid on desktop */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0', position: 'relative' }}>
            {/* Connector line */}
            <div style={{ position: 'absolute', top: '28px', left: '10%', right: '10%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(249,115,22,0.3), rgba(249,115,22,0.3), transparent)', pointerEvents: 'none' }} className="hidden md:block" />

            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 100}>
                <div style={{ padding: '0 12px', textAlign: 'center', position: 'relative' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(249,115,22,0.1)', border: '2px solid rgba(249,115,22,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', position: 'relative', zIndex: 1 }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#F97316', fontFamily: 'monospace' }}>{step.num}</span>
                  </div>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'white', marginBottom: '8px', lineHeight: 1.3 }}>{step.title}</h3>
                  <p style={{ fontSize: '12px', color: '#64748B', lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY JOIN US ── */}
      <section style={{ padding: '88px 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '14px' }}>Why Incade</span>
              <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1 }}>
                Why join us
              </h2>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            {WHY_JOIN.map((item, i) => (
              <Reveal key={item.title} delay={i * 70}>
                <div
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px 24px', transition: 'border-color 0.25s, transform 0.25s', cursor: 'default' }}
                  onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'rgba(249,115,22,0.25)'; el.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'rgba(255,255,255,0.06)'; el.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <item.Icon size={20} style={{ color: '#F97316' }} />
                  </div>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>{item.title}</h3>
                  <p style={{ fontSize: '13px', color: '#64748B', lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── OPEN POSITIONS ── */}
      <section ref={jobsRef} style={{ padding: '88px 24px', background: 'rgba(255,255,255,0.01)', borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '14px' }}>Open Positions</span>
              <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1 }}>
                Current openings
              </h2>
            </div>
          </Reveal>

          {/* Filters */}
          <Reveal delay={80}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '36px', justifyContent: 'center' }}>
              {[
                { value: dept, setter: setDept, options: DEPARTMENTS, icon: <Briefcase size={14} /> },
                { value: loc, setter: setLoc, options: LOCATIONS, icon: <MapPin size={14} /> },
                { value: type, setter: setType, options: TYPES, icon: <Filter size={14} /> },
              ].map((filter, fi) => (
                <div key={fi} style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                    {filter.icon}
                  </div>
                  <select
                    value={filter.value}
                    onChange={(e) => filter.setter(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 36px 10px 34px', color: '#94A3B8', fontSize: '13px', cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none' }}
                  >
                    {filter.options.map((o) => <option key={o} value={o} style={{ background: '#0F172A' }}>{o}</option>)}
                  </select>
                  <ChevronDown size={14} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', pointerEvents: 'none' }} />
                </div>
              ))}
            </div>
          </Reveal>

          {/* Empty state */}
          <Reveal delay={120}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '20px', padding: '64px 32px', textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <Briefcase size={28} style={{ color: '#F97316', opacity: 0.7 }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: 'white', marginBottom: '10px' }}>No open positions right now</h3>
              <p style={{ fontSize: '15px', color: '#64748B', marginBottom: '6px' }}>But we&apos;re always interested in exceptional people.</p>
              <p style={{ fontSize: '14px', color: '#475569' }}>Send us your application below and we&apos;ll keep you in mind.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── APPLICATION FORM ── */}
      <section ref={formRef} style={{ padding: '88px 24px' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '14px' }}>Apply</span>
              <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1, marginBottom: '16px' }}>
                Send your application
              </h2>
              <p style={{ fontSize: '15px', color: '#64748B' }}>We read every application. You&apos;ll hear back within 5 business days.</p>
            </div>
          </Reveal>

          {submitted ? (
            <Reveal>
              <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '20px', padding: '56px 40px', textAlign: 'center' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(16,185,129,0.12)', border: '2px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <CheckCircle size={36} style={{ color: '#10B981' }} />
                </div>
                <h3 style={{ fontSize: '22px', fontWeight: 800, color: 'white', marginBottom: '12px' }}>Application received!</h3>
                <p style={{ fontSize: '15px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '8px' }}>
                  Thank you for applying to Incade. We&apos;ve received your application and will review it carefully.
                </p>
                <p style={{ fontSize: '14px', color: '#64748B' }}>Expect to hear back within 5 business days.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={80}>
              <form
                onSubmit={handleSubmit}
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '20px', padding: '40px' }}
                noValidate
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  {/* Full Name */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '8px' }}>
                      Full Name <span style={{ color: '#F97316' }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Jane Smith"
                      value={form.fullName}
                      onChange={(e) => { setForm((f) => ({ ...f, fullName: e.target.value })); setErrors((er) => ({ ...er, fullName: '' })); }}
                      style={{ ...inputStyle, borderColor: errors.fullName ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = 'rgba(249,115,22,0.4)'; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = errors.fullName ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'; }}
                    />
                    {errors.fullName && <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{errors.fullName}</p>}
                  </div>

                  {/* Email */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '8px' }}>
                      Email Address <span style={{ color: '#F97316' }}>*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="jane@example.com"
                      value={form.email}
                      onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setErrors((er) => ({ ...er, email: '' })); }}
                      style={{ ...inputStyle, borderColor: errors.email ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = 'rgba(249,115,22,0.4)'; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = errors.email ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'; }}
                    />
                    {errors.email && <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{errors.email}</p>}
                  </div>

                  {/* Phone with country code */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '8px' }}>
                      Phone Number <span style={{ color: '#F97316' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <select
                          value={form.countryCode}
                          onChange={(e) => setForm((f) => ({ ...f, countryCode: e.target.value }))}
                          style={{ ...inputStyle, width: 'auto', paddingRight: '32px', appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code} value={c.code} style={{ background: '#0F172A' }}>{c.code} {c.label}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', pointerEvents: 'none' }} />
                      </div>
                      <input
                        type="tel"
                        placeholder="555 000 0000"
                        value={form.phone}
                        onChange={(e) => { setForm((f) => ({ ...f, phone: e.target.value })); setErrors((er) => ({ ...er, phone: '' })); }}
                        style={{ ...inputStyle, flex: 1, borderColor: errors.phone ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
                        onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = 'rgba(249,115,22,0.4)'; }}
                        onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = errors.phone ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'; }}
                      />
                    </div>
                    {errors.phone && <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{errors.phone}</p>}
                  </div>

                  {/* LinkedIn */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '8px' }}>
                      LinkedIn or Portfolio URL <span style={{ color: '#F97316' }}>*</span>
                    </label>
                    <input
                      type="url"
                      placeholder="https://linkedin.com/in/yourname"
                      value={form.linkedin}
                      onChange={(e) => { setForm((f) => ({ ...f, linkedin: e.target.value })); setErrors((er) => ({ ...er, linkedin: '' })); }}
                      style={{ ...inputStyle, borderColor: errors.linkedin ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = 'rgba(249,115,22,0.4)'; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = errors.linkedin ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'; }}
                    />
                    {errors.linkedin && <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{errors.linkedin}</p>}
                  </div>

                  {/* Motivation */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '8px' }}>
                      Why do you want to join Incade? <span style={{ color: '#F97316' }}>*</span>
                    </label>
                    <textarea
                      rows={5}
                      placeholder="Tell us what excites you about Incade and what you'd bring to the team..."
                      value={form.motivation}
                      onChange={(e) => { setForm((f) => ({ ...f, motivation: e.target.value })); setErrors((er) => ({ ...er, motivation: '' })); }}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '120px', borderColor: errors.motivation ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
                      onFocus={(e) => { (e.target as HTMLTextAreaElement).style.borderColor = 'rgba(249,115,22,0.4)'; }}
                      onBlur={(e) => { (e.target as HTMLTextAreaElement).style.borderColor = errors.motivation ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'; }}
                    />
                    {errors.motivation && <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{errors.motivation}</p>}
                  </div>

                  {/* CV Upload */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '8px' }}>
                      Upload CV / Resume <span style={{ color: '#F97316' }}>*</span>
                    </label>
                    <label
                      htmlFor="cv-upload"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: 'rgba(255,255,255,0.03)',
                        border: `1px dashed ${errors.cv ? 'rgba(239,68,68,0.5)' : form.cv ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: '10px',
                        padding: '16px 20px',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLLabelElement).style.background = 'rgba(249,115,22,0.04)'; (e.currentTarget as HTMLLabelElement).style.borderColor = 'rgba(249,115,22,0.3)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLLabelElement).style.background = 'rgba(255,255,255,0.03)'; (e.currentTarget as HTMLLabelElement).style.borderColor = errors.cv ? 'rgba(239,68,68,0.5)' : form.cv ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.15)'; }}
                    >
                      {form.cv ? (
                        <CheckCircle size={20} style={{ color: '#10B981', flexShrink: 0 }} />
                      ) : (
                        <Upload size={20} style={{ color: '#64748B', flexShrink: 0 }} />
                      )}
                      <div>
                        <p style={{ fontSize: '13px', color: form.cv ? '#10B981' : '#94A3B8', fontWeight: 600 }}>
                          {form.cv ? form.cv.name : 'Click to upload your CV or resume'}
                        </p>
                        <p style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>PDF, DOC, DOCX — max 10MB</p>
                      </div>
                    </label>
                    <input id="cv-upload" type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{ display: 'none' }} />
                    {errors.cv && <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{errors.cv}</p>}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{ width: '100%', background: submitting ? 'rgba(249,115,22,0.5)' : 'linear-gradient(135deg,#F97316,#EA580C)', color: 'white', padding: '14px 28px', borderRadius: '10px', fontSize: '15px', fontWeight: 700, border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  {submitting ? (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>Apply Now <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: '88px 24px', background: 'rgba(255,255,255,0.01)', borderTop: '1px solid rgba(255,255,255,0.04)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 50% at 50% 100%, rgba(249,115,22,0.07), transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
          <Reveal>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 42px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1, marginBottom: '16px' }}>
              Don&apos;t see a role that fits?
            </h2>
            <p style={{ fontSize: '16px', color: '#94A3B8', marginBottom: '36px', lineHeight: 1.65 }}>
              We&apos;re always open to exceptional talent. If you believe you can make Incade better, we want to hear from you.
            </p>
            <button
              onClick={() => scrollTo(formRef)}
              style={{ background: 'linear-gradient(135deg,#F97316,#EA580C)', color: 'white', padding: '14px 32px', borderRadius: '10px', fontSize: '15px', fontWeight: 700, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
            >
              Send Your Application <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .col-span-12 { grid-column: span 12; }
        @media (min-width: 768px) {
          .md\\:col-span-7 { grid-column: span 7; }
          .md\\:col-span-5 { grid-column: span 5; }
          .md\\:block { display: block; }
          .hidden { display: none; }
        }
      `}</style>
    </div>
  );
}
